import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/shared/accordion';
import { loadOpportunityDefinitionsFromCSV } from '@/utils/csvLoader';

// Empty fallback definitions since CSV is the sole source of truth
const fallbackDefinitions: { [key: string]: { description: string; whyItMatters: string } } = {};

// Cache for loaded definitions
let definitionsCache: { [key: string]: { description: string; whyItMatters: string } } | null = null;
let isLoadingDefinitions = false;

// Function to load definitions with caching
const loadDefinitions = async (): Promise<{ [key: string]: { description: string; whyItMatters: string } }> => {
  if (definitionsCache) {
    return definitionsCache;
  }

  if (isLoadingDefinitions) {
    // Wait for the current loading to complete
    while (isLoadingDefinitions) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return definitionsCache || fallbackDefinitions;
  }

  isLoadingDefinitions = true;

  try {
    console.log('Loading opportunity definitions from CSV (sole source of truth)...');
    definitionsCache = await loadOpportunityDefinitionsFromCSV();
    console.log('Successfully loaded definitions from CSV');
  } catch (error) {
    console.log('Failed to load from CSV, using empty fallback definitions');
    definitionsCache = fallbackDefinitions;
  }

  isLoadingDefinitions = false;
  return definitionsCache;
};

// Export the definitions for backward compatibility
export const definitions = fallbackDefinitions;

interface OpportunityDefinitionProps {
  name: string;
  defaultOpen?: boolean;
}

const OpportunityDefinition = ({ name, defaultOpen }: OpportunityDefinitionProps) => {
  const [description, setDescription] = useState<string>('Loading...');
  const [whyItMatters, setWhyItMatters] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDefinition = async () => {
      try {
        const definitions = await loadDefinitions();
        const loadedDefinition = definitions[name];
        if (loadedDefinition) {
          setDescription(loadedDefinition.description);
          setWhyItMatters(loadedDefinition.whyItMatters);
        } else {
          setDescription('Definition not available.');
          setWhyItMatters('');
        }
      } catch (error) {
        console.error('Error loading definition:', error);
        setDescription('Definition not available.');
        setWhyItMatters('');
      } finally {
        setIsLoading(false);
      }
    };

    loadDefinition();
  }, [name]);

  return (
    <Accordion type="single" collapsible className="w-full" defaultValue={defaultOpen ? name : undefined}>
      <AccordionItem value={name} className="border-none">
        <AccordionTrigger className="hover:no-underline py-0">
          <span className="text-base font-medium">{name}</span>
        </AccordionTrigger>
        <AccordionContent>
          <div className="pt-2 space-y-2">
            <div className="text-sm text-muted-foreground">
              {isLoading ? 'Loading definition...' : description}
            </div>
            {!isLoading && whyItMatters && (
              <div className="text-sm text-muted-foreground italic">
                {whyItMatters}
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default OpportunityDefinition;